/**
 * Stock (plan §5, test-plan §1a `BoardStock`): sheet stock keeps the first slice's
 * `StockProfile` (birch ply with its back sheet); board stock is solid timber sold in
 * planed boards up to `maxWidthTicks` wide, so wider parts become glue-ups.
 *
 * Every generator reads stock through `resolveStock`, which gives one shape for both kinds.
 */
import { z } from 'zod';
import { STOCK, StockProfileSchema, stockLabels, type StockProfile } from '../domain/profiles';
import { fmtMm } from '../domain/units';
import sheeshamJson from '../../profiles/stock/sheesham-board-25.json' with { type: 'json' };
import oakJson from '../../profiles/stock/oak-board-20.json' with { type: 'json' };
import sheesham38Json from '../../profiles/stock/sheesham-board-38.json' with { type: 'json' };
import sheesham45Json from '../../profiles/stock/sheesham-board-45.json' with { type: 'json' };
import oak38Json from '../../profiles/stock/oak-board-38.json' with { type: 'json' };
import oak45Json from '../../profiles/stock/oak-board-45.json' with { type: 'json' };
import sheesham70Json from '../../profiles/stock/sheesham-board-70.json' with { type: 'json' };
import oak70Json from '../../profiles/stock/oak-board-70.json' with { type: 'json' };

const ticks = z.int().positive();

export const BoardStockSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().min(1),
  version: z.string().min(1),
  kind: z.literal('board'),
  species: z.string().min(1),
  material: z.string().min(1),
  nominalDescription: z.string().min(1),
  edgeDescription: z.string().min(1),
  thicknessTicks: ticks,
  nominalThicknessTicks: ticks.optional(),
  /** The widest single board the stock is sold at; wider parts are glued up from strips. */
  maxWidthTicks: ticks,
  maxLengthTicks: ticks,
  grainAxis: z.literal('length'),
  finishSpec: z.string().min(1),
  /** kg/m³, for the mass advisory. */
  density: z.number().positive().optional(),
  /** The sheet back used with this stock (solid backs are a later option). */
  back: z.object({
    description: z.string().min(1),
    thicknessTicks: ticks,
    nominalThicknessTicks: ticks.optional(),
    sheet: z.object({ lengthTicks: ticks, widthTicks: ticks }),
    grainAxis: z.enum(['length', 'width']),
    allowRotation: z.boolean(),
  }),
  notes: z.string().optional(),
});
export type BoardStock = z.infer<typeof BoardStockSchema>;

/** Either stock kind, as stored in `profiles/stock/`. */
export type AnyStock = StockProfile | BoardStock;
export const isBoardStock = (s: AnyStock): s is BoardStock => (s as BoardStock).kind === 'board';

/** One shape for both kinds (ticks). */
export interface ResolvedStock {
  id: string;
  version: string;
  kind: 'sheet' | 'board';
  species: string;
  /** Material text for carcass parts ("Birch plywood 18 mm, B/BB, clear matt lacquer"). */
  label: string;
  /** Short material name for grouping and the Look step ("Birch plywood", "Solid sheesham"). */
  material: string;
  thicknessTicks: number;
  nominalThicknessTicks: number;
  faceGrade: string;
  finishSpec: string;
  density: number | null;
  /** Sheet stock: the sheet; board stock: null. */
  sheet: { lengthTicks: number; widthTicks: number } | null;
  allowRotation: boolean;
  maxWidthTicks: number | null;
  maxLengthTicks: number | null;
  edgeText: (arrisMm: string) => string;
  back: {
    label: string;
    thicknessTicks: number;
    nominalThicknessTicks: number;
    sheet: { lengthTicks: number; widthTicks: number };
    grainAxis: 'length' | 'width';
    allowRotation: boolean;
    faceText: string;
  };
  /** The profile as stored (written into packs and JSON). */
  source: AnyStock;
}

export function resolveStock(s: AnyStock): ResolvedStock {
  if (isBoardStock(s)) {
    const nominal = s.nominalThicknessTicks ?? s.thicknessTicks;
    const backNominal = s.back.nominalThicknessTicks ?? s.back.thicknessTicks;
    return {
      id: s.id, version: s.version, kind: 'board', species: s.species,
      label: s.nominalDescription, material: s.material,
      thicknessTicks: s.thicknessTicks, nominalThicknessTicks: nominal,
      faceGrade: 'planed', finishSpec: s.finishSpec, density: s.density ?? null,
      sheet: null, allowRotation: false, maxWidthTicks: s.maxWidthTicks, maxLengthTicks: s.maxLengthTicks,
      edgeText: () => s.edgeDescription,
      back: {
        label: s.back.description, thicknessTicks: s.back.thicknessTicks, nominalThicknessTicks: backNominal,
        sheet: s.back.sheet, grainAxis: s.back.grainAxis, allowRotation: s.back.allowRotation,
        faceText: `${s.back.description}; B face to the front`,
      },
      source: s,
    };
  }
  const labels = stockLabels(s);
  return {
    id: s.id, version: s.version, kind: 'sheet', species: s.material,
    label: labels.label, material: s.material,
    thicknessTicks: s.measuredThicknessTicks, nominalThicknessTicks: labels.nominalThicknessTicks,
    faceGrade: s.faceGrade, finishSpec: s.finishSpec, density: 680,
    sheet: s.sheet, allowRotation: s.allowRotation, maxWidthTicks: null, maxLengthTicks: null,
    edgeText: (arris) => `Exposed ply, sanded arris ${arris} mm`,
    back: {
      label: labels.backLabel, thicknessTicks: s.backThicknessTicks, nominalThicknessTicks: labels.nominalBackThicknessTicks,
      sheet: s.sheet, grainAxis: s.grainAxis, allowRotation: s.allowRotation,
      faceText: `${s.material} ${fmtMm(labels.nominalBackThicknessTicks)} mm, ${s.faceGrade}; B face to the front`,
    },
    source: s,
  };
}

/** Carcass face text for a part ("Birch plywood 18 mm, B/BB; B face out"). */
export function faceText(r: ResolvedStock, side: 'out' | 'up'): string {
  if (r.kind === 'board') return `${r.species} ${fmtMm(r.nominalThicknessTicks)} mm, best face ${side === 'out' ? 'out' : 'up'}; grain along the length`;
  return `${r.material} ${fmtMm(r.nominalThicknessTicks)} mm, ${r.faceGrade}; B face ${side}`;
}

function mustParseBoard(json: unknown): BoardStock {
  const r = BoardStockSchema.safeParse(json);
  if (!r.success) throw new Error(`Bundled board stock is invalid: ${r.error.issues[0]?.path.join('.')}: ${r.error.issues[0]?.message}`);
  return Object.freeze(r.data);
}

export const parseBoardStock = (json: unknown) => BoardStockSchema.safeParse(json);
export const parseAnyStock = (json: unknown): { ok: true; value: AnyStock } | { ok: false; error: string } => {
  const kind = (json as { kind?: string } | null)?.kind;
  const r = kind === 'board' ? BoardStockSchema.safeParse(json) : StockProfileSchema.safeParse(json);
  return r.success ? { ok: true, value: r.data } : { ok: false, error: r.error.issues[0]?.message ?? 'invalid' };
};

/** Bundled stock, by id. The first is the default. */
export const STOCKS: Readonly<Record<string, AnyStock>> = Object.freeze({
  [STOCK.id]: STOCK,
  'sheesham-board-25': mustParseBoard(sheeshamJson),
  'oak-board-20': mustParseBoard(oakJson),
  // thick solid frames (FND-23): same widths and glue-up rule as the species' standard board
  'sheesham-board-38': mustParseBoard(sheesham38Json),
  'sheesham-board-45': mustParseBoard(sheesham45Json),
  'oak-board-38': mustParseBoard(oak38Json),
  'oak-board-45': mustParseBoard(oak45Json),
  // chunky slabs (pilot-v1-r3 p14): two boards face-laminated to 70 mm
  'sheesham-board-70': mustParseBoard(sheesham70Json),
  'oak-board-70': mustParseBoard(oak70Json),
});

/** Thick variants per species (FND-23), thinnest first: the stock the fit picks for a chunky frame. */
export const THICK_STOCKS: Readonly<Record<'sheesham' | 'oak', readonly string[]>> = Object.freeze({
  sheesham: ['sheesham-board-25', 'sheesham-board-38', 'sheesham-board-45', 'sheesham-board-70'],
  oak: ['oak-board-20', 'oak-board-38', 'oak-board-45', 'oak-board-70'],
});

/**
 * The stock for a board thickness read from the photo (mm): the species from the material
 * words (oak, else sheesham), the nearest of its thicknesses; null under 32 mm (keep the default).
 * 58 mm and up reads as a laminated 70 mm slab.
 */
export function stockForThickness(thicknessMm: number, materialWords = ''): string | null {
  if (!(thicknessMm >= 32)) return null;
  const list = /\boak\b/i.test(materialWords) ? THICK_STOCKS.oak : THICK_STOCKS.sheesham;
  return thicknessMm >= 58 ? list[3]! : thicknessMm >= 41.5 ? list[2]! : list[1]!;
}

/** Stock by id, or null. */
export const stockById = (id: string | null | undefined): AnyStock | null => (id && STOCKS[id]) || null;

/** The materials the Look step offers (one stock each in v1). */
export const MATERIAL_CHOICES: readonly { id: string; label: string; stockId: string; note: string; thicker?: readonly { stockId: string; label: string }[] }[] = Object.freeze([
  { id: 'birch-ply', label: 'Birch plywood', stockId: 'birch-ply-18', note: '18 mm sheets, exposed ply edges' },
  { id: 'sheesham', label: 'Sheesham', stockId: 'sheesham-board-25', note: '25 mm solid boards, glued up where wider than 200 mm', thicker: [{ stockId: 'sheesham-board-38', label: '38 mm' }, { stockId: 'sheesham-board-45', label: '45 mm' }, { stockId: 'sheesham-board-70', label: '70 mm slab' }] },
  { id: 'oak', label: 'Oak', stockId: 'oak-board-20', note: '20 mm solid boards, glued up where wider than 180 mm', thicker: [{ stockId: 'oak-board-38', label: '38 mm' }, { stockId: 'oak-board-45', label: '45 mm' }, { stockId: 'oak-board-70', label: '70 mm slab' }] },
]);
